import React from "react";
import { Box, Chip } from "@mui/material";
import { InputString } from "../../../common/controls";
import { parsePersonName } from "../../../../common/utils/parsePersonName";

const LABELS_NOMBRE = ["1er Nombre", "2do Nombre", "3er Nombre"];
const LABELS_APELLIDO = ["1er Apellido", "2do Apellido", "3er Apellido"];

type Props = {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
};

function compose(apellidos: string, nombres: string): string {
  const a = apellidos.trim();
  const n = nombres.trim();
  if (!a && !n) return "";
  if (!n) return a;
  return `${a}, ${n}`;
}

function splitValue(value: string): { apellidos: string; nombres: string } {
  const idx = value.indexOf(",");
  if (idx === -1) return { apellidos: value, nombres: "" };
  return { apellidos: value.substring(0, idx).trim(), nombres: value.substring(idx + 1).trim() };
}

export const PersonaFisicaNameFields: React.FC<Props> = ({
  value,
  onChange,
  error,
  helperText,
}) => {
  const [apellidos, setApellidos] = React.useState("");
  const [nombres, setNombres] = React.useState("");
  const initializedRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (initializedRef.current === value) return;
    initializedRef.current = value;
    if (!value) { setApellidos(""); setNombres(""); return; }
    const parts = splitValue(value);
    setApellidos(parts.apellidos);
    setNombres(parts.nombres);
  }, [value]);

  const handleApellidos = (val: string) => {
    setApellidos(val);
    const composed = compose(val, nombres);
    initializedRef.current = composed;
    onChange(composed);
  };

  const handleNombres = (val: string) => {
    setNombres(val);
    const composed = compose(apellidos, val);
    initializedRef.current = composed;
    onChange(composed);
  };

  const composed = compose(apellidos, nombres);
  const parsed = React.useMemo(() => {
    if (!composed || !composed.includes(",")) return null;
    return parsePersonName(composed);
  }, [composed]);

  return (
    <>
      <InputString value={nombres} onChange={handleNombres} label="Nombre/s" error={error && !nombres} helperText={error && !nombres ? helperText : undefined} />
      {parsed && parsed.nombres.length > 0 && (
        <Box sx={{ mt: 0.5, mb: 1.5, ml: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {parsed.nombres.map((n, i) => (
            <Chip key={`n-${i}`} label={`${LABELS_NOMBRE[i]}: ${n}`} size="small" variant="outlined" color="secondary" sx={{ fontSize: "0.75rem" }} />
          ))}
        </Box>
      )}
      <InputString value={apellidos} onChange={handleApellidos} label="Apellido/s" error={error && !apellidos} helperText={error && !apellidos ? helperText : undefined} />
      {parsed && parsed.apellidos.length > 0 && (
        <Box sx={{ mt: 0.5, mb: 1.5, ml: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
          {parsed.apellidos.map((a, i) => (
            <Chip key={`a-${i}`} label={`${LABELS_APELLIDO[i]}: ${a}`} size="small" variant="outlined" color="primary" sx={{ fontSize: "0.75rem" }} />
          ))}
        </Box>
      )}
    </>
  );
};
